import { Link } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "@phosphor-icons/react";
import { Reveal, AnimatedCounter } from "@/components/Motion";
import { FAQ_ITEMS } from "@/components/FAQ";
import Seo, { faqSchema } from "@/components/Seo";
import RedCrossBadge from "@/components/RedCrossBadge";
import PromoPopup from "@/components/PromoPopup";
import SeatsLeft from "@/components/SeatsLeft";

const HERO_VIDEO = "/media/hero.mp4";
const HERO_POSTER = "/media/hero-poster.jpg";
const BANNER = "https://static.wixstatic.com/media/324075_648035abc8cd435d91a8f495bb444742~mv2.png";

export const STATE_SEALS = {
  MD: "/img/state-seals/md.png",
  NJ: "https://upload.wikimedia.org/wikipedia/commons/thumb/8/8d/Seal_of_New_Jersey.svg/960px-Seal_of_New_Jersey.svg.png",
  DC: "/img/state-seals/dc.png",
  AZ: "https://upload.wikimedia.org/wikipedia/commons/thumb/0/09/State_Seal_of_Arizona.svg/960px-State_Seal_of_Arizona.svg.png",
  VA: "https://upload.wikimedia.org/wikipedia/commons/thumb/6/6f/Seal_of_Virginia.svg/960px-Seal_of_Virginia.svg.png",
  FL: "https://upload.wikimedia.org/wikipedia/commons/thumb/2/2b/Seal_of_Florida.svg/960px-Seal_of_Florida.svg.png",
  UT: "/img/state-seals/ut.png",
};

const STATE_PERMIT = {
  MD: { name: "Maryland",        permit: "HWCP" },
  NJ: { name: "New Jersey",      permit: "PCH"  },
  DC: { name: "Washington D.C.", permit: "CPL"  },
  AZ: { name: "Arizona",         permit: "CCW"  },
  VA: { name: "Virginia",        permit: "CHP"  },
  FL: { name: "Florida",         permit: "CWL"  },
  UT: { name: "Utah",            permit: "CFP"  },
};

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [allCourses, setAllCourses] = useState([]);
  const heroRef = useRef(null);
  const { scrollY } = useScroll();
  const heroParallax = useTransform(scrollY, [0, 800], [0, 200]);
  const heroFade = useTransform(scrollY, [0, 500], [1, 0.2]);

  useEffect(() => {
    api.get("/courses").then(({ data }) => {
      setAllCourses(data);
      setFeatured(data.filter(c => c.is_featured).slice(0, 3));
    }).catch(() => {});
  }, []);

  const wcCourses = allCourses.filter(c => c.category === "Wear & Carry");
  const cprCourse = allCourses.find(c => c.slug === "first-aid-cpr-aed");

  return (
    <div data-testid="home-page" className="overflow-x-hidden">
      <PromoPopup />
      <Seo
        title="State-Certified Firearms & Security Training Academy"
        description="Maryland-based training academy. Wear & Carry permits (MD · NJ · DC · VA · AZ · FL · UT), Armed Security Officer, D.C. Special Police, and CPR/AED — taught by active-duty law-enforcement instructors S. Williams and Derek Kinard. Established 2024."
        path="/"
        schema={faqSchema(FAQ_ITEMS)}
      />

      {/* ============== HERO ============== */}
      <section ref={heroRef} className="relative h-screen min-h-[720px] flex items-center overflow-hidden" data-testid="hero">
        <motion.div className="absolute inset-0" style={{ y: heroParallax, opacity: heroFade }}>
          <video src={HERO_VIDEO} poster={HERO_POSTER} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" data-testid="hero-video" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/70 to-black/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/40" />
        </motion.div>

        <div className="container-x relative z-10 grid grid-cols-12 gap-8 items-center">
          <div className="col-span-12 lg:col-span-7">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="mb-6">
              <div className="overline flex items-center gap-3" data-testid="hero-overline">
                <span className="w-10 h-px bg-[#FFD300]" />
                TRAINING ACADEMY · MARYLAND · EST. 2024
              </div>
            </motion.div>

            <h1 className="font-heading text-5xl sm:text-6xl lg:text-8xl leading-[0.88] tracking-[-0.03em] mb-8" data-testid="hero-headline">
              {[
                { t: "Certified.", c: "" },
                { t: "Trained.", c: "text-[#FFD300]" },
                { t: "Trusted.", c: "" },
              ].map((line, i) => (
                <motion.div
                  key={line.t}
                  initial={{ opacity: 0, y: 60, skewY: 3 }}
                  animate={{ opacity: 1, y: 0, skewY: 0 }}
                  transition={{ duration: 0.8, delay: 0.2 + i * 0.15, ease: [0.21, 0.47, 0.32, 0.98] }}
                  className={line.c}
                >
                  {line.t}
                </motion.div>
              ))}
            </h1>

            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1, duration: 0.6 }} className="max-w-xl text-base md:text-lg text-neutral-300 leading-relaxed" data-testid="hero-subtext">
              State-certified firearms and security training for civilians, security officers, and protective agents —
              across MD · NJ · DC · VA · AZ · FL · UT.
            </motion.p>
          </div>

          <div className="col-span-12 lg:col-span-5">
            <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.8, duration: 0.7 }} className="grid grid-cols-1 gap-4" data-testid="hero-path-cards">
              <Link to="/training" className="group relative bg-black/70 backdrop-blur-md border border-white/15 hover:border-[#FFD300] p-8 transition-all overflow-hidden" data-testid="hero-path-training">
                <img src="/img/brand/academy.png" alt="" aria-hidden className="absolute -right-8 -bottom-8 w-52 h-auto opacity-20 group-hover:opacity-40 transition-opacity pointer-events-none" />
                <div className="flex items-start justify-between gap-4 relative">
                  <div className="flex items-start gap-5">
                    <img src="/img/brand/academy.png" alt="Secrecy Executive Training Academy" className="h-20 w-auto flex-shrink-0 drop-shadow-lg" data-testid="hero-logo-academy" />
                    <div>
                      <div className="overline text-[#FFD300] mb-1">START TODAY</div>
                      <div className="font-heading text-3xl tracking-tight leading-tight">Training Academy</div>
                      <div className="text-neutral-400 text-sm mt-2 leading-relaxed">
                        Wear &amp; Carry permits (7 states) · Armed Security · D.C. Special Police · CPR / AED · eLearning
                      </div>
                    </div>
                  </div>
                  <ArrowRight size={22} className="text-neutral-500 group-hover:text-[#FFD300] group-hover:translate-x-1 transition-all flex-shrink-0" />
                </div>
              </Link>

              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }} className="flex items-center justify-between text-xs font-mono tracking-[0.14em] uppercase text-neutral-400 pt-1">
                <span>Questions? Call <a href="tel:+14106450722" className="text-white hover:text-[#FFD300]">410-645-0722</a></span>
                <Link to="/contact" className="text-[#FFD300] hover:underline" data-testid="hero-contact-btn">Contact us →</Link>
              </motion.div>
            </motion.div>
          </div>
        </div>

        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3, duration: 0.7 }} className="absolute bottom-0 left-0 right-0 border-t border-white/15 bg-black/40 backdrop-blur" data-testid="hero-stats">
          <div className="container-x grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
            {[
              { num: 16, suffix: "", label: "Courses" },
              { num: 7, suffix: "", label: "States" },
              { num: 72, suffix: "+", label: "Class dates" },
              { num: 100, suffix: "%", label: "Pass-rate" },
            ].map((s) => (
              <div key={s.label} className="py-6 px-6 first:pl-0">
                <div className="font-heading text-3xl md:text-4xl tracking-tighter text-[#FFD300]">
                  <AnimatedCounter to={s.num} suffix={s.suffix} />
                </div>
                <div className="overline mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* ============== WEAR & CARRY STATES ============== */}
      <section className="border-b border-white/10 bg-[#050505] section-pad" data-testid="wc-section">
        <div className="container-x">
          <Reveal>
            <div className="flex items-end justify-between mb-14 flex-wrap gap-4">
              <div>
                <div className="overline mb-3">// 001 — AVAILABLE COURSES</div>
                <h2 className="font-heading text-4xl md:text-6xl tracking-tighter leading-[0.95]">
                  Seven states.<br/><span className="text-[#FFD300]">One academy.</span>
                </h2>
              </div>
              <Link to="/training" className="btn-ghost" data-testid="view-all-wc">View all courses <ArrowRight size={14} /></Link>
            </div>
          </Reveal>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-px bg-white/10">
            {["MD", "NJ", "DC", "AZ", "VA", "FL", "UT"].map((st, i) => {
              const course = wcCourses.find(c => c.state === st);
              const info = STATE_PERMIT[st];
              return (
                <Reveal key={st} delay={i * 0.06}>
                  <Link to={course ? `/training/${course.slug}` : "/training"} className="block bg-black p-6 group hover:bg-[#0E0E0E] transition-colors text-center h-full" data-testid={`wc-state-${st}`}>
                    <div className="aspect-square mb-4 flex items-center justify-center overflow-hidden bg-gradient-to-br from-white to-neutral-100 rounded-full p-3 mx-auto" style={{ width: '85%' }}>
                      <motion.img
                        src={STATE_SEALS[st]}
                        alt={`Official ${info.name} state seal`}
                        whileHover={{ scale: 1.06, rotate: 2 }}
                        transition={{ duration: 0.4 }}
                        className="w-full h-full object-contain"
                        data-testid={`wc-seal-${st}`}
                      />
                    </div>
                    <div className="font-heading text-2xl tracking-tighter mb-1">{st}</div>
                    <div className="font-mono text-[10px] tracking-[0.18em] text-neutral-400 uppercase">{info.name}</div>
                    <div className="mt-2 inline-block code-badge">{info.permit}</div>
                    <div className="mt-3 font-mono text-[10px] tracking-[0.2em] text-[#FFD300] opacity-0 group-hover:opacity-100 transition-opacity">
                      {course ? `$${course.price.toFixed(0)} →` : "VIEW →"}
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============== RED CROSS — FIRST AID / CPR / AED ============== */}
      <section className="section-pad" data-testid="red-cross-section">
        <div className="container-x">
          <Reveal>
            <Link to={cprCourse ? `/training/${cprCourse.slug}` : "/training"} className="group block border border-white/10 hover:border-[#ED1B2E] bg-[#0A0A0A] transition-colors" data-testid="red-cross-card">
              <div className="grid grid-cols-12 gap-8 items-center p-8 md:p-12">
                <div className="col-span-12 md:col-span-3 flex md:justify-center">
                  <RedCrossBadge size={120} withText={false} />
                </div>
                <div className="col-span-12 md:col-span-6">
                  <div className="overline mb-3">// 002 — MEDICAL / FIRST RESPONSE</div>
                  <h2 className="font-heading text-3xl md:text-5xl tracking-tighter leading-[0.95] mb-4">
                    American Red Cross<br/><span className="text-[#ED1B2E]">First Aid · CPR · AED</span>
                  </h2>
                  <p className="text-neutral-400 leading-relaxed max-w-xl">
                    Nationally recognized Red Cross certification, taught by our law-enforcement instructor team.
                    Adult &amp; pediatric skills, hands-on practice, digital certificate valid for two years.
                  </p>
                </div>
                <div className="col-span-12 md:col-span-3 md:text-right">
                  {cprCourse && <div className="font-heading text-4xl mb-2">${cprCourse.price.toFixed(0)}</div>}
                  <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#FFD300] inline-flex items-center gap-2 group-hover:translate-x-1 transition-transform">Enroll <ArrowRight size={14} /></span>
                </div>
              </div>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ============== FEATURED COURSES ============== */}
      <section className="bg-[#050505] border-y border-white/10 section-pad" data-testid="academy-section">
        <div className="container-x">
          <Reveal>
            <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
              <div>
                <div className="overline mb-4">// 003 — TRAINING ACADEMY</div>
                <h2 className="font-heading text-4xl md:text-6xl tracking-tighter leading-[0.95]">Featured courses.</h2>
              </div>
              <Link to="/training" className="btn-ghost" data-testid="view-all-courses">Entire catalog <ArrowRight size={14} /></Link>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10">
            {featured.map((c, i) => (
              <Reveal key={c.id} delay={i * 0.1}>
                <Link to={`/training/${c.slug}`} className="bg-black p-8 lift block group h-full relative overflow-hidden" data-testid={`featured-course-${c.slug}`}>
                  {c.is_special && (
                    <div className="absolute top-5 -right-12 rotate-45 bg-[#FF7A00] text-black font-mono text-[10px] font-bold tracking-[0.2em] uppercase py-1.5 w-44 text-center shadow-lg z-10 pointer-events-none" data-testid="special-ribbon">
                      {c.promo_label || "Special"}
                    </div>
                  )}
                  <div className="aspect-[4/3] w-full mb-6 overflow-hidden bg-neutral-900 relative img-zoom-parent">
                    {c.state && STATE_SEALS[c.state] ? (
                      <div className="w-full h-full bg-gradient-to-br from-[#FFD300] via-[#fff] to-[#FFD300] flex items-center justify-center p-10 img-zoom">
                        <img src={STATE_SEALS[c.state]} alt={c.state} className="max-w-full max-h-full object-contain drop-shadow-xl" />
                      </div>
                    ) : (
                      <img src={c.image} alt={c.title} className="w-full h-full object-cover img-zoom" />
                    )}
                  </div>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="code-badge">{c.code}</span>
                    {c.state && <span className="state-tag">{c.state}</span>}
                  </div>
                  <h3 className="font-heading text-2xl tracking-tighter mb-3 leading-tight">{c.title}</h3>
                  <p className="text-neutral-400 text-sm leading-relaxed mb-6">{c.short_description}</p>
                  <SeatsLeft next={c.next_session} className="mb-3 block" />
                  <div className="flex items-center justify-between border-t border-white/10 pt-4">
                    <div className="font-heading text-3xl">${c.price.toFixed(0)}</div>
                    <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#FFD300] group-hover:translate-x-1 transition-transform">Enroll →</span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============== FINAL CTA ============== */}
      <section className="relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0">
          <img src={BANNER} alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-black/40" />
        </div>
        <div className="container-x relative py-32">
          <Reveal>
            <div className="overline mb-6">// SIGNAL</div>
            <h2 className="font-heading text-5xl md:text-8xl tracking-tighter leading-[0.85] max-w-5xl mb-10">
              Ready to earn<br/>your certification?<br/>
              <span className="text-[#FFD300]">Class dates open now.</span>
            </h2>
            <div className="flex flex-wrap gap-4">
              <Link to="/training" className="btn-primary text-lg py-5 px-10" data-testid="cta-enroll-final">Enroll Now <ArrowRight size={16} /></Link>
              <Link to="/contact" className="btn-secondary text-lg py-5 px-10" data-testid="cta-contact-final">Contact Us <ArrowRight size={16} /></Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
